import React, { useState } from 'react';
import { Helmet } from '../components/Helmet';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Trophy, User, Star, Mail, Briefcase, Phone, Calendar, MessageSquare, Users } from 'lucide-react';
import { coursesData } from '../data/courses';

const DemandeInfo = () => {
  const [searchParams] = useSearchParams();
  const paramId = Number(searchParams.get('id'));
  const currentCourse = coursesData.find(c => c.id === paramId) || coursesData[0];
  const sousTitre = `${currentCourse.duration} · ${currentCourse.participants} · Formation ${currentCourse.category}`;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    participants: '10 à 15',
    courseId: currentCourse.id,
    date: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    interface WindowWithGtag extends Window {
      gtag_report_conversion?: () => void;
    }

    if (typeof window !== 'undefined' && (window as WindowWithGtag).gtag_report_conversion) {
      (window as WindowWithGtag).gtag_report_conversion?.();
    }

    alert('Merci ! Votre demande de devis a bien été envoyée.');
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formContent = (
    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100">
      <h2 className="text-2xl font-bold mb-6 text-gray-900">Demande de Devis</h2>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Nom complet */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom complet *</label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              placeholder="Votre nom"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email professionnel *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              placeholder="vous@entreprise.com"
            />
          </div>
        </div>

        {/* Entreprise */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Entreprise *</label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              name="company"
              required
              value={formData.company}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              placeholder="Nom de votre entreprise"
            />
          </div>
        </div>

        {/* Téléphone */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              placeholder="06 12 34 56 78"
            />
          </div>
        </div>

        {/* Nombre de participants */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nombre de participants</label>
          <div className="relative">
            <Users className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <select
              name="participants"
              value={formData.participants}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none bg-white"
            >
              <option value="Moins de 10">Moins de 10</option>
              <option value="10 à 20">10 à 20</option>
              <option value="20 à 50">20 à 50</option>
              <option value="Plus de 50">Plus de 50</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
        </div>

        {/* Formation */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Formation souhaitée</label>
          <div className="relative">
            <select
              name="courseId"
              value={formData.courseId}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none bg-white"
            >
              {coursesData.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}

            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
        </div>

        {/* Date souhaitée */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date souhaitée</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              placeholder="Ex: Septembre 2024"
            />
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <textarea
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none resize-none"
              placeholder="Précisez votre besoin..."
            ></textarea>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-primary text-white font-bold py-4 px-6 rounded-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 duration-200 text-lg"
        >
          Envoyer ma demande
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          Les informations transmises via ce formulaire sont utilisées par Upnov IA pour répondre à votre demande et assurer le suivi correspondant. Pour en savoir plus sur le traitement de vos données et vos droits, consultez notre{" "}
          <Link to="/politique-de-confidentialite/" className="underline hover:text-gray-700">
            Politique de confidentialité
          </Link>.
        </p>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Helmet>
        <title>{currentCourse.title}</title>
        <meta name="description" content={currentCourse.description} />
        <meta property="og:title" content={currentCourse.title} />
        <meta property="og:description" content={currentCourse.description} />
        <meta property="og:url" content="https://www.upnov.com/DemandeInfo" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/blogimg/team-building-ia.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <script>
          {`
      function gtag_report_conversion() {
        console.log('envoi conversion')
        if (typeof gtag === 'function') {
          gtag('event', 'conversion', {
            send_to: 'AW-998086090/e5KICN_0qOIbEMqr9tsD',
            value: 1.0,
            currency: 'EUR'
          });
          console.log('conversion envoyée')
        }
      }
    `}
        </script>
      </Helmet>


      {/* Simplified Header - No external navigation */}
      <header className="bg-white py-4 shadow-sm sticky top-0 z-50">
        <div className="container-custom flex justify-center md:justify-start">
          <div className="flex items-center gap-2">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
              {currentCourse.title.includes("Formation IA") ? (
                <>
                  Formation IA <span className="text-primary">{currentCourse.title.replace("Formation IA", "").trim()}</span>
                </>
              ) : (
                currentCourse.title
              )}
            </h1>
          </div>
        </div>
      </header>

      <main className="container-custom py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left Column: Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-10"
          >
            {/* Hero Text */}
            <div>

              <p className="text-xl md:text-2xl text-gray-800 font-medium mb-4">
                {sousTitre}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {currentCourse.description}
              </p>
            </div>

            {/* Mobile Form - Visible only on mobile */}
            <div className="lg:hidden">
              {formContent}
            </div>

            {/* Solution Section */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-primary"></div>
              <h3 className="text-2xl font-bold mb-6 flex items-center text-gray-900">
                <Trophy className="w-7 h-7 text-primary mr-3" />
                {currentCourse.title}
              </h3>
              <ul className="space-y-4 mb-8">
                {[currentCourse.level, currentCourse.duration, currentCourse.participants].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="bg-green-100 p-1 rounded-full mr-3 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center text-primary font-semibold bg-primary/5 p-4 rounded-xl">
                <User className="w-6 h-6 mr-3" />
                Animé par un expert IA entreprise
              </div>
            </div>

            {/* Social Proof */}
            <div className="space-y-6">
              <div className="flex flex-wrap gap-8 items-center">
                <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-100 text-center flex-1">
                  <p className="text-3xl font-bold text-primary mb-1">+1300</p>
                  <p className="text-sm text-gray-600 font-medium">professionnels formés</p>
                </div>
                <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-100 text-center flex-1">
                  <p className="text-3xl font-bold text-primary mb-1">98%</p>
                  <p className="text-sm text-gray-600 font-medium">de satisfaction</p>
                </div>
              </div>

              {/* Logos placeholder - simulating client logos */}
              <div className="pt-4">
                <p className="text-sm text-gray-500 font-medium mb-4 uppercase tracking-wider">Ils nous font confiance</p>
                <div className="flex flex-wrap gap-6 opacity-50 grayscale items-center">

                  <img src="/logo/logo-edf.webp" alt="EDF" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-ratp.webp" alt="RATP" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-orano.webp" alt="Orano" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-loreal.webp" alt="L'Oréal" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-thales.webp" alt="Thales" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-vinci.webp" alt="Vinci" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-eiffage.webp" alt="Eiffage" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-sncf.webp" alt="SNCF" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-sagem.webp" alt="Sagem" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                  <img src="/logo/logo-carrefour.webp" alt="Carrefour" width="140" height="64" className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Bénéfices</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentCourse.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <Star className="w-5 h-5 text-yellow-400 mr-3 shrink-0 fill-yellow-400" />
                    <span className="text-gray-800 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right Column: Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block lg:sticky lg:top-24"
          >
            {formContent}
          </motion.div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-white py-8 mt-12 border-t border-gray-100">
        <div className="container-custom text-center">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Upnov IA. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default DemandeInfo;
